import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CoolLocalStorage, CoolSessionStorage } from '@angular-cool/storage';
import { Observable, ReplaySubject } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { set } from 'lodash';
import { WindowService } from 'shared/services/window/window.service';
import { GenesysCloudApiConfig, GenesysCloudApiEndpoints } from './genesys-cloud-api-config.model';
import { APP_SUBDOMAIN_MATCHER, GenesysCloudDomains } from 'environments/environment.helper';
import { bcSessionKeys } from '../common/constants/session-keys';
import { localStorageKeys } from '../common/constants/local-storage-keys';
import { CanonicalEnvironmentName } from './canonical-environment-name.enum';

interface UriObject {
  uri: string;
}

interface ServiceDiscoveryProperties {
  environment?: string;
  api?: UriObject;
  auth?: UriObject;
  purecloud?: UriObject;
}

@Injectable({
  providedIn: 'root'
})
export class GenesysCloudConfigService {
  private static readonly ServiceDiscoveryPropertiesKeyValueRegExp = new RegExp('^(?<key>[^=]+)=(?<value>.*)$');
  private static readonly prodLikeEnvNamePattern = new RegExp('^prod(-\\w{1,10})?$');
  private readonly genesysCloudDomain = this.determineGenesysCloudDomainFromHostname();
  private readonly apiConfigSubject = new ReplaySubject<GenesysCloudApiConfig>(1);
  private readonly serviceDiscoveryProperties$ = this.getServiceDiscoveryProperties$().pipe(share());
  readonly apiConfig$ = this.apiConfigSubject.asObservable();
  readonly apiEndpoints$: Observable<GenesysCloudApiEndpoints> = this.serviceDiscoveryProperties$.pipe(map(properties => ({
    apiUri: properties.api?.uri,
    authUri: properties.auth?.uri,
    purecloudUri: properties.purecloud?.uri
  })));
  readonly environment$ = this.serviceDiscoveryProperties$.pipe(
    map(properties => properties.environment)
  );
  readonly canonicalEnvironmentName$ = this.environment$.pipe(
    map(envName => this.mapToCanonicalEnvironmentName(envName))
  );
  static readonly boldDomainMatcher = {
    domain: GenesysCloudDomains.dca,
    matcher: /bold360[.]io/ // will removed once we move to the apps.x.com env
  };

  constructor(private readonly sessionStorageService: CoolSessionStorage,
              private readonly localStorageService: CoolLocalStorage,
              private readonly http: HttpClient,
              private readonly windowService: WindowService) {
  }

  determineGenesysCloudDomainFromHostname(): string {
    const hostname = this.windowService.location.host;
    if (GenesysCloudConfigService.boldDomainMatcher.matcher.test(hostname)) {
      return GenesysCloudConfigService.boldDomainMatcher.domain;
    }
    return this.removeSubdomainForHostname(hostname);
  }

  getConfigFromStorage(): GenesysCloudApiConfig {
    const apiConfig: GenesysCloudApiConfig = this.sessionStorageService.tryGetObject(bcSessionKeys.storage.genesysCloudApiConfig);
    return apiConfig && this.isValidConfig(apiConfig) ? apiConfig : null;
  }

  saveConfigToStorage(accessToken: string, expiresAt: number, apiEndpoints: GenesysCloudApiEndpoints): void {
    this.sessionStorageService.setObject(bcSessionKeys.storage.genesysCloudApiConfig, { accessToken, expiresAt, apiEndpoints, domain: this.genesysCloudDomain });
  }

  clearConfigFromStorage(): void {
    this.sessionStorageService.removeItem(bcSessionKeys.storage.genesysCloudApiConfig);
    this.localStorageService.removeItem(localStorageKeys.smartAdvisorToken);
  }

  emitApiConfig(config: GenesysCloudApiConfig): void {
    this.apiConfigSubject.next(config);
  }

  mapToCanonicalEnvironmentName(envName: string): CanonicalEnvironmentName {
    if (GenesysCloudConfigService.prodLikeEnvNamePattern.test(envName)) {
      return CanonicalEnvironmentName.Prod;
    } else if (CanonicalEnvironmentName.Test === envName) {
      return CanonicalEnvironmentName.Test;
    } else if (CanonicalEnvironmentName.Dev === envName) {
      return CanonicalEnvironmentName.Dev;
    }

    throw new Error(`No canonical name found for environment: ${envName}`);
  }

  private static processServiceDiscoveryResponse(responseText: string): ServiceDiscoveryProperties {
    const lines = responseText.split('\n').filter(line => !!line);
    return lines.reduce((result, line) => {
      const match = GenesysCloudConfigService.ServiceDiscoveryPropertiesKeyValueRegExp.exec(line);
      if (match?.groups?.key) {
        set(result, match.groups.key, match.groups.value);
      }

      return result;
    }, {});
  }

  private getServiceDiscoveryProperties$(): Observable<ServiceDiscoveryProperties> {
    const configUrl = `https://apps.${this.genesysCloudDomain}/services/discovery.properties`;
    const httpOptions: Object = {
      responseType: 'text'
    };

    return this.http.get<string>(configUrl, httpOptions).pipe(
      map(GenesysCloudConfigService.processServiceDiscoveryResponse)
    );
  }

  private removeSubdomainForHostname(hostname: string): string {
    return hostname.replace(APP_SUBDOMAIN_MATCHER, '');
  }

  private isValidConfig(apiConfigFromStorage: GenesysCloudApiConfig): boolean {
    return apiConfigFromStorage.domain === this.genesysCloudDomain && apiConfigFromStorage.expiresAt > Date.now();
  }
}
